/**
 * useHrIssue188 — Admin-Web hooks for Issue #188 HR additions.
 *
 * 红线：
 *   - 仅使用 generated @jinzhuan/api-client；不手写 fetch。
 *   - 不维护"前端假状态"：每个 mutation 都用后端返回值刷新本地缓存；
 *     接口错误转 toast 类信号，不静默吞掉。
 *   - 响应中不出现 cipher / hash 字段；仅展示 last4 + 状态。
 */
import { useCallback, useState } from 'react';
import { createApiClient } from '@/lib/adminApiClient';
import type { ApiComponents } from '@/lib/adminApiClient';
import { getStoredAccessToken } from '@/lib/tokenStorage';

const apiClient = createApiClient({
  baseUrl: '',
  getAccessToken: () => getStoredAccessToken(),
});

// ---------------------------------------------------------------------------
// Types re-exported from generated client
// ---------------------------------------------------------------------------

export type HrAttendanceProviderDto = ApiComponents['schemas']['HrAttendanceProviderDto'];
export type HrAttendanceProviderListDto = ApiComponents['schemas']['HrAttendanceProviderListDto'];
export type HrAttendanceSyncRunDto = ApiComponents['schemas']['HrAttendanceSyncRunDto'];
export type HrAttendanceSyncRunListDto = ApiComponents['schemas']['HrAttendanceSyncRunListDto'];
export type HrAttendanceRecordDto = ApiComponents['schemas']['HrAttendanceRecordDto'];
export type HrAttendanceRecordListDto = ApiComponents['schemas']['HrAttendanceRecordListDto'];
export type HrAttendanceAdjustmentDto = ApiComponents['schemas']['HrAttendanceAdjustmentDto'];
export type HrAttendanceAdjustmentListDto =
  ApiComponents['schemas']['HrAttendanceAdjustmentListDto'];

export type HrCandidateDto = ApiComponents['schemas']['HrCandidateDto'];
export type HrCandidateListDto = ApiComponents['schemas']['HrCandidateListDto'];
export type HrInterviewTemplateDto = ApiComponents['schemas']['HrInterviewTemplateDto'];
export type HrInterviewTemplateListDto = ApiComponents['schemas']['HrInterviewTemplateListDto'];
export type HrInterviewScheduleDto = ApiComponents['schemas']['HrInterviewScheduleDto'];
export type HrInterviewScheduleListDto =
  ApiComponents['schemas']['HrInterviewScheduleListDto'];

export type HrTrainingCourseDto = ApiComponents['schemas']['HrTrainingCourseDto'];
export type HrTrainingCourseListDto = ApiComponents['schemas']['HrTrainingCourseListDto'];
export type HrTrainingRecordDto = ApiComponents['schemas']['HrTrainingRecordDto'];
export type HrTrainingRecordListDto = ApiComponents['schemas']['HrTrainingRecordListDto'];

export interface HrApiError {
  readonly message: string;
  readonly status?: number;
}

function toApiError(err: unknown, httpStatus?: number): HrApiError {
  if (httpStatus === 409) {
    return { message: '记录已被其他操作修改或状态不符，请刷新后重试', status: httpStatus };
  }
  if (httpStatus === 403) return { message: '没有权限执行此操作', status: httpStatus };
  if (httpStatus === 400) return { message: '请求参数不合法，请检查必填项与字段格式', status: httpStatus };
  if (httpStatus === 404) return { message: '资源不存在或不可访问', status: httpStatus };
  if (err instanceof Error) return { message: err.message, status: httpStatus };
  return { message: String(err), status: httpStatus };
}

// ---------------------------------------------------------------------------
// Attendance sync runs hook
// ---------------------------------------------------------------------------

export interface ListAttendanceSyncRunFilter {
  providerId?: string;
  providerType?: HrAttendanceProviderDto['providerType'];
  status?: HrAttendanceSyncRunDto['status'];
  page?: number;
  limit?: number;
}

export function useHrAttendanceSyncRuns() {
  const [items, setItems] = useState<HrAttendanceSyncRunDto[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<HrApiError | null>(null);

  const fetchRuns = useCallback(async (filter?: ListAttendanceSyncRunFilter) => {
    setLoading(true);
    setError(null);
    try {
      const query: Record<string, unknown> = {};
      if (filter?.providerId) query.providerId = filter.providerId;
      if (filter?.providerType) query.providerType = filter.providerType;
      if (filter?.status) query.status = filter.status;
      if (filter?.page) query.page = filter.page;
      if (filter?.limit) query.limit = filter.limit;
      const { data, error: apiErr, response } = await apiClient.GET(
        '/api/v1/hr/attendance/sync-runs',
        { params: { query } },
      );
      if (apiErr) {
        setError(toApiError(apiErr, (response as Response)?.status));
        return null;
      }
      if (data) {
        setItems([...data.items]);
        setTotal(data.total);
      }
      return data ?? null;
    } catch (e) {
      setError(toApiError(e));
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  return { items, total, loading, error, fetchRuns };
}

// ---------------------------------------------------------------------------
// Candidate hook
// ---------------------------------------------------------------------------

export interface ListCandidateFilter {
  stage?: HrCandidateDto['stage'];
  appliedPosition?: string;
  nameContains?: string;
  page?: number;
  limit?: number;
}

export function useHrCandidates() {
  const [items, setItems] = useState<HrCandidateDto[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<HrApiError | null>(null);

  const fetchCandidates = useCallback(async (filter?: ListCandidateFilter) => {
    setLoading(true);
    setError(null);
    try {
      const query: Record<string, unknown> = {};
      if (filter?.stage) query.stage = filter.stage;
      if (filter?.appliedPosition) query.appliedPosition = filter.appliedPosition;
      if (filter?.nameContains) query.nameContains = filter.nameContains;
      if (filter?.page) query.page = filter.page;
      if (filter?.limit) query.limit = filter.limit;
      const { data, error: apiErr, response } = await apiClient.GET(
        '/api/v1/hr/candidates',
        { params: { query } },
      );
      if (apiErr) {
        setError(toApiError(apiErr, (response as Response)?.status));
        return null;
      }
      if (data) {
        setItems([...data.items]);
        setTotal(data.total);
      }
      return data ?? null;
    } catch (e) {
      setError(toApiError(e));
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCandidate = useCallback(async (id: string) => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: apiErr, response } = await apiClient.GET(
        '/api/v1/hr/candidates/{id}',
        { params: { path: { id } } },
      );
      if (apiErr) {
        setError(toApiError(apiErr, (response as Response)?.status));
        return null;
      }
      return data ?? null;
    } catch (e) {
      setError(toApiError(e));
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  return { items, total, loading, error, fetchCandidates, fetchCandidate };
}

// ---------------------------------------------------------------------------
// Interview templates hook
// ---------------------------------------------------------------------------

export function useHrInterviewTemplates() {
  const [items, setItems] = useState<HrInterviewTemplateDto[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<HrApiError | null>(null);

  const fetchTemplates = useCallback(
    async (filter?: {
      kind?: HrInterviewTemplateDto['kind'];
      channel?: HrInterviewTemplateDto['channel'];
      enabled?: boolean;
      page?: number;
      limit?: number;
    }) => {
      setLoading(true);
      setError(null);
      try {
        const query: Record<string, unknown> = {};
        if (filter?.kind) query.kind = filter.kind;
        if (filter?.channel) query.channel = filter.channel;
        if (typeof filter?.enabled === 'boolean') query.enabled = filter.enabled;
        if (filter?.page) query.page = filter.page;
        if (filter?.limit) query.limit = filter.limit;
        const { data, error: apiErr, response } = await apiClient.GET(
          '/api/v1/hr/interview-templates',
          { params: { query } },
        );
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        if (data) {
          setItems([...data.items]);
          setTotal(data.total);
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { items, total, loading, error, fetchTemplates };
}

// ---------------------------------------------------------------------------
// Interview schedules hook
// ---------------------------------------------------------------------------

export function useHrInterviewSchedules() {
  const [items, setItems] = useState<HrInterviewScheduleDto[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<HrApiError | null>(null);

  const fetchSchedules = useCallback(
    async (filter?: {
      candidateId?: string;
      status?: HrInterviewScheduleDto['status'];
      from?: string;
      to?: string;
      page?: number;
      limit?: number;
    }) => {
      setLoading(true);
      setError(null);
      try {
        const query: Record<string, unknown> = {};
        if (filter?.candidateId) query.candidateId = filter.candidateId;
        if (filter?.status) query.status = filter.status;
        if (filter?.from) query.from = filter.from;
        if (filter?.to) query.to = filter.to;
        if (filter?.page) query.page = filter.page;
        if (filter?.limit) query.limit = filter.limit;
        const { data, error: apiErr, response } = await apiClient.GET(
          '/api/v1/hr/interview-schedules',
          { params: { query } },
        );
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        if (data) {
          setItems([...data.items]);
          setTotal(data.total);
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { items, total, loading, error, fetchSchedules };
}

// ---------------------------------------------------------------------------
// Training records hook
// ---------------------------------------------------------------------------

export function useHrTrainingRecords() {
  const [items, setItems] = useState<HrTrainingRecordDto[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<HrApiError | null>(null);

  const fetchRecords = useCallback(
    async (filter?: {
      courseId?: string;
      employeeId?: string;
      status?: HrTrainingRecordDto['status'];
      page?: number;
      limit?: number;
    }) => {
      setLoading(true);
      setError(null);
      try {
        const query: Record<string, unknown> = {};
        if (filter?.courseId) query.courseId = filter.courseId;
        if (filter?.employeeId) query.employeeId = filter.employeeId;
        if (filter?.status) query.status = filter.status;
        if (filter?.page) query.page = filter.page;
        if (filter?.limit) query.limit = filter.limit;
        const { data, error: apiErr, response } = await apiClient.GET(
          '/api/v1/hr/training-records',
          { params: { query } },
        );
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        if (data) {
          setItems([...data.items]);
          setTotal(data.total);
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { items, total, loading, error, fetchRecords };
}

// ---------------------------------------------------------------------------
// Label helpers
// ---------------------------------------------------------------------------

export const HR_ATTENDANCE_SYNC_STATUS_LABELS: Record<
  HrAttendanceSyncRunDto['status'],
  string
> = {
  running: '同步中',
  succeeded: '成功',
  partial: '部分成功',
  failed: '失败',
};

export const HR_CANDIDATE_STAGE_LABELS: Record<HrCandidateDto['stage'], string> = {
  new: '新简历',
  screening: '待筛选',
  screen_passed: '初筛通过',
  interview_invited: '邀约面试',
  interviewed: '已面试',
  second_interview: '复试',
  hired: '录用',
  rejected: '淘汰',
  talent_pool: '人才库',
};

export const HR_INTERVIEW_STATUS_LABELS: Record<
  HrInterviewScheduleDto['status'],
  string
> = {
  draft: '草稿',
  invited: '已邀约',
  confirmed: '已确认',
  rescheduled: '已改期',
  completed: '已完成',
  canceled: '已撤销',
  no_show: '未到场',
};

export const HR_TRAINING_RECORD_STATUS_LABELS: Record<
  HrTrainingRecordDto['status'],
  string
> = {
  assigned: '已指派',
  in_progress: '进行中',
  completed: '已完成',
  failed: '未通过',
  exempted: '免修',
  canceled: '已撤销',
};
